import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@restworld/feature-authentication';

import { imageVariant } from '../utils/image-variant';

export const useAvatarImage = () => {
  const { user } = useAuth();
  const picture = user?.picture;

  // The URLs to try, in order, before falling back to the placeholder:
  //   1. the `_sm` variant (256px long edge, ~6 KB): the navbar renders it at ~32-48px. A
  //      backfilled picture is a public S3 URL; a legacy one is `/img/<key>/sm`, which asks the
  //      backend to find the original, generate the size and 301-redirect to it. The backend
  //      sends that redirect with `cache-control: public, max-age=86400`, so the browser itself
  //      remembers the resolved S3 URL for later pages and reloads.
  //   2. the stored picture itself, for when the variant cannot be produced (the original is
  //      stored under a name `/img` does not look for). Heavier, but only ever loaded after (1)
  //      has failed.
  // A stored value that is not a URL at all (22 workers have `{:ok, :nil_image}`) yields no
  // candidate, so no request is made for it.
  //
  // This used to be fetched as a blob through `/workers/asset`, which only reads S3 URLs: every
  // `/img/...` URL failed there with a 400 and the avatar silently stayed empty.
  const candidates = useMemo(() => {
    if (!picture || !/^https?:\/\//.test(picture)) return [];
    return Array.from(new Set([imageVariant(picture, 'sm'), picture]));
  }, [picture]);

  // Failures are recorded per URL, not counted: at desktop width both overlay navs are mounted
  // (the mobile one only hidden by CSS), so one broken URL fires `onError` twice. A counter
  // would skip a candidate; adding the same URL twice is a no-op.
  const [failedURLs, setFailedURLs] = useState<ReadonlySet<string>>(new Set());

  // A new picture (upload, re-login) starts from the first candidate again.
  useEffect(() => {
    setFailedURLs(new Set());
  }, [picture]);

  const onAvatarImageError = useCallback((url?: string) => {
    if (!url) return;
    setFailedURLs((failed) => (failed.has(url) ? failed : new Set(failed).add(url)));
  }, []);

  return {
    avatarImageURL: candidates.find((url) => !failedURLs.has(url)),
    onAvatarImageError,
    avatarName: user?.name || user?.business_name,
  };
};
